import { SummaryType } from "@ls/grouping-service";
import { exchange } from "@ls/js-fetch";
import GroupingInput from "state/Constants/GroupingInput";
import ScaleType from "state/Constants/ScaleType";
import SelectionState from "state/selections/SelectionState";
import {
  selectGetGroupingParams,
  selectSideBySideValue,
  selectScenarioValue,
} from "state/selections/selectors";
import { getRatioTypeDefinitions, splitByCalcType } from "state/util";
import scope from "./scope";
import { selectDefinitions } from "./selectors";

export const DELETE_DEFINITION = `${scope}/DELETE_DEFINITION`;
export const SET_DEFINITION = `${scope}/SET_DEFINITION`;
export const setDefinition = (idx, definition) => ({
  type: SET_DEFINITION,
  idx,
  definition,
});

export const SET_DEFINITIONS = `${scope}/SET_DEFINITIONS`;
export const setDefinitions = (definitions) => ({
  type: SET_DEFINITIONS,
  definitions,
});

export const REQUEST_DATA = `${scope}/REQUEST_DATA`;
export const RECEIVE_DATA = `${scope}/RECEIVE_DATA`;
export const REQUEST_RATIO_DATA = `${scope}/REQUEST_RATIO_DATA`;
export const RECEIVE_RATIO_DATA = `${scope}/RECEIVE_RATIO_DATA`;
export const REQUEST_SBS_DATA = `${scope}/REQUEST_SBS_DATA`;
export const RECEIVE_SBS_DATA = `${scope}/RECEIVE_SBS_DATA`;
export const requestGridData = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const definitions = selectDefinitions(SelectionState.COMMITTED)(state);
    if (!definitions?.length) {
      return;
    }

    const [absDefinitions, bmkDefinitions, relDefinitions] =
      splitByCalcType(definitions);
    const getGroupingParams = selectGetGroupingParams(SelectionState.COMMITTED)(
      state,
    );
    const scenarioValue = selectScenarioValue(SelectionState.COMMITTED)(state);
    const sideBySideValue = selectSideBySideValue(SelectionState.COMMITTED)(
      state,
    );
    const isPercentileScale = (scaleType) =>
      scaleType === "25" ||
      scaleType === "50" ||
      scaleType === "75" ||
      scaleType === ScaleType[50];

    const request = async (summary, definitions, benchesToo) => {
      dispatch({ type: REQUEST_DATA, summary });

      const percentileDefinitions = definitions.filter(({ scaleType }) =>
        isPercentileScale(scaleType),
      );

      const regularDefinitions = definitions.filter(({ scaleType }) => {
        return !isPercentileScale(scaleType) && scaleType !== ScaleType.Ratio;
      });

      let params = getGroupingParams({
        summary,
        benchesToo,
        analytics: [
          ...new Set(regularDefinitions.map(({ analytic }) => analytic)),
        ],
        inputs: !!definitions.find(
          (def) => def.scaleType === ScaleType.WeightedAverage,
        )
          ? [GroupingInput.BUCKETS]
          : [],
        ...(percentileDefinitions.length > 0 && {
          percentile_analytics: [
            ...new Set(percentileDefinitions.map(({ analytic }) => analytic)),
          ],
          percentiles: [
            ...new Set(percentileDefinitions.map(({ scaleType }) => scaleType)),
          ],
        }),
      });

      dispatch({
        type: RECEIVE_DATA,
        summary,
        data: (
          await exchange({
            params,
            cancelId: `data-${scope}-${summary}`,
            url: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
          })
        )?.data,
      });
      if (!!scenarioValue && !!sideBySideValue) {
        params = { ...params, workspace: scenarioValue };
        dispatch({ type: REQUEST_SBS_DATA, summary });
        dispatch({
          type: RECEIVE_SBS_DATA,
          summary,
          data: (
            await exchange({
              url: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
              params,
              cancelId: `data-sbs-${scope}-${summary}`,
            })
          )?.data,
        });
      }
    };

    const requestRatio = async (summary, definitions, benchesToo) => {
      dispatch({ type: REQUEST_RATIO_DATA, summary });

      const params = {
        ...getGroupingParams({
          summary,
          benchesToo,
          analytics: [...new Set(definitions.map(({ analytic }) => analytic))],
          inputs: !!definitions.find(
            (def) => def.scaleType === ScaleType.WeightedAverage,
          )
            ? [GroupingInput.BUCKETS]
            : [],
        }),
        relative: true,
        relativeTypes: "RATIO",
      };

      dispatch({
        type: RECEIVE_RATIO_DATA,
        summary,
        data: (
          await exchange({
            params,
            cancelId: `data-ratio-${scope}-${summary}`,
            url: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
          })
        )?.data,
      });
    };

    if (absDefinitions.length || bmkDefinitions.length) {
      request(
        SummaryType.ABSOLUTE,
        [...absDefinitions, ...bmkDefinitions],
        bmkDefinitions.length > 0,
      ).then();
    }

    if (relDefinitions.length) {
      request(SummaryType.RELATIVE, relDefinitions, true).then();
    }

    if (getRatioTypeDefinitions(definitions).length && relDefinitions.length) {
      requestRatio(
        SummaryType.RELATIVE,
        getRatioTypeDefinitions(definitions),
        true,
      ).then();
    }
  };
};
